import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute, Link } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Communication: Chat & Messaging Options
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Clear and effective communication is essential for success in Mythea.
      Whether you are strategizing with your kingdom, coordinating attacks, or
      engaging in diplomatic discussions, staying connected is key.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      There are several ways to communicate with other provinces within the
      game. While debates and diplomacy can sometimes become heated, all players
      are expected to remain civil. If you experience harassment or
      inappropriate behavior, you can report it to the Admin Province (Kingdom
      1:1).
    </p>
  </div>
);

const GameChatSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Using Game Chat</h3>
    <p className="text-[#b8b8b8] mb-2">
      You can access the Game Chat by clicking the blue chat icon located at the
      top right of each page.
    </p>
    <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
      Chat Channels
    </h4>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        <strong>World Chat</strong> – A global chat where all online provinces
        can engage in discussion and ask questions.
      </li>
      <li>
        Your game location remains hidden, and it is advisable not to reveal it.
      </li>
      <li>
        Game events such as wars, dragon attacks, and kingdom updates will
        appear here.
      </li>
      <li>
        You can mute notifications in the settings menu or by using the mute
        button in the chat box.
      </li>
      <li>
        <strong>Kingdom Chat</strong> – A private chat for all online members of
        your kingdom.
      </li>
      <li>
        Used for strategizing, coordinating attacks, and planning defenses.
      </li>
      <li>
        Incoming enemy attacks and outgoing operations will be displayed here.
      </li>
    </ul>
  </div>
);

const ChatCommandsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Chat Commands</h3>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-white/10">
            <th className="p-2 text-[#f0d060]">Command</th>
            <th className="p-2 text-[#f0d060]">Effect</th>
          </tr>
        </thead>
        <tbody>
          {[
            {
              command: '!register xxxx',
              effect:
                'Sets xxxx as your unique chat nickname (can only be changed once every 24 hours).',
            },
            {
              command: '!join xxxx',
              effect:
                'Joins the specified channel xxxx. (New channel names must be alphanumeric.)',
            },
            {
              command: '!help',
              effect: 'Displays a list of available chat commands.',
            },
            {
              command: '!play songname',
              effect:
                'Plays a song of your choice. (Songs can be broadcast to everyone in the channel, except in World Chat.)',
            },
            {
              command: '!calc 1+1',
              effect: 'Performs a quick calculation using the chat calculator.',
            },
          ].map((row, index) => (
            <tr
              key={row.command}
              className={`border-b border-[rgba(212,175,55,0.12)] transition-colors duration-150 hover:bg-gray-700/30 ${
                index % 2 === 0 ? 'bg-white/[0.03]' : 'bg-white/[0.03]'
              }`}
            >
              <td className="p-2 text-yellow-300 font-mono">{row.command}</td>
              <td className="p-2 text-[#b8b8b8]">{row.effect}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ForumsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Game Forums</h3>
    <p className="text-[#b8b8b8] mb-2">
      The Game Forums provide a structured way to engage in discussions, share
      knowledge, and coordinate with your kingdom.
    </p>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-white/10">
            <th className="p-2 text-[#f0d060]">Forum Name</th>
            <th className="p-2 text-[#f0d060]">Purpose</th>
          </tr>
        </thead>
        <tbody>
          {[
            {
              name: 'Announcements',
              use: 'Used by Admins to post game updates. Posts in this forum will display your chat name.',
            },
            {
              name: 'Private Kingdom Forum',
              use: 'Accessible only to your kingdom, used for discussing strategy and kingdom affairs.',
            },
            {
              name: 'World General Chat Forum',
              use: 'Open to all players for polite discussions on any subject.',
            },
            {
              name: 'Recruitment Forum',
              use: 'For kingdoms seeking new members or players looking for a new kingdom.',
            },
            {
              name: 'War Forum',
              use: 'Available only during wartime, visible to both warring kingdoms for battle-related discussions.',
            },
          ].map((row, index) => (
            <tr
              key={row.name}
              className={`border-b border-[rgba(212,175,55,0.12)] transition-colors duration-150 hover:bg-gray-700/30 ${
                index % 2 === 0 ? 'bg-white/[0.03]' : 'bg-white/[0.03]'
              }`}
            >
              <td className="p-2 text-yellow-300">{row.name}</td>
              <td className="p-2 text-[#b8b8b8]">{row.use}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const MessagesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Private Messages</h3>
    <p className="text-[#b8b8b8] mb-2">
      You can send direct messages to other provinces using the Messages page.
      All previous messages from the Age are stored. New messages trigger
      notifications so you won&apos;t miss important communications. To report
      issues or ask questions, message the Admin Province (Kingdom 1:1).
    </p>
  </div>
);

const NotesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Adding Notes</h3>
    <p className="text-[#b8b8b8] mb-2">
      Notes help kingdoms stay organized. You can attach notes to:
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>A Kingdom Page – For kingdom-wide coordination.</li>
      <li>A Specific Province – To keep track of enemies or allies.</li>
      <li>
        Market Items – If your market is large enough, notes can help with trade
        and logistics.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      Only members of your kingdom can view and edit these notes.
    </p>
  </div>
);

const FormattingSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Text Formatting & Color
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      Formatting can be used in chat, forums, kingdom mottos, and Monarch
      messages. All formatting commands should be closed with [/] to apply
      correctly. If you have requests for additional formatting options, contact
      Admin (Kingdom 1:1).
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
      {[
        'bpink',
        'pink',
        'red',
        'blue',
        'green',
        'white',
        'yellow',
        'black',
        'big',
        'small',
        'b',
        'bred',
        'bblue',
        'byellow',
        'bgreen',
        'bwhite',
        'i',
        'center',
        'bold',
        'orange',
        'borange',
        'cyan',
        'box',
        'hr',
        'purple',
        'bpurple',
        'lime',
        'blime',
        'u',
      ].map((format) => (
        <div
          key={format}
          className="bg-white/[0.03] p-2 rounded border border-white/10 text-center"
        >
          <code className="text-yellow-300">[{format}]</code>
        </div>
      ))}
    </div>
  </div>
);

const DiscordIntegrationSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Discord Integration
    </h3>
    <p className="text-[#b8b8b8] mb-2 leading-relaxed">
      In addition to in-game chat and forums, Mythea provides extensive Discord integration.
      World Chat is mirrored live to Discord (<code className="text-yellow-300 font-mono">#world-chat</code>),
      the official bot supports 14 slash commands for intel, war plans, and calculations,
      monarchs can set up kingdom webhooks for attack alarms, and linked players can receive
      direct message alerts when under attack.
    </p>
    <div className="flex flex-wrap gap-3 pt-2">
      <Link
        to="/discord"
        className="inline-flex items-center gap-2 rounded-lg border border-[#d4af37]/35 bg-[#d4af37]/15 px-4 py-2 text-sm font-semibold text-[#f0d060] transition-colors hover:bg-[#d4af37]/25"
      >
        <span>Explore Full Discord Guide &rarr;</span>
      </Link>
      <Link
        to="/classic-guide/discord"
        className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-[#b8b8b8] transition-colors hover:bg-white/10 hover:text-white"
      >
        <span>Classic Guide View</span>
      </Link>
    </div>
  </div>
);

export const Route = createFileRoute('/communication/')({
  component: CommunicationPage,
});

function CommunicationPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/communication" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/messages@2x.png"
              alt="Communication"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Communication</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <GameChatSection />
            <ChatCommandsSection />
            <ForumsSection />
            <MessagesSection />
            <NotesSection />
            <FormattingSection />
            <DiscordIntegrationSection />
          </div>
        </section>
      </div>
    </div>
  );
}
